import type { SupabaseClient } from "@supabase/supabase-js";
import { supabase } from "@/lib/supabase";
import { fetchAllPagesBounded } from "@/lib/api/boundedListFetch";

async function currentUserIsOrgAdmin(): Promise<boolean> {
  const { data: auth } = await supabase.auth.getUser();
  const uid = auth.user?.id;
  if (!uid) return false;
  const { data } = await supabase.from("users").select("user_type").eq("id", uid).maybeSingle();
  const row = data as { user_type: string } | null;
  return row?.user_type === "orgadmin";
}

/** Assessment tables + RPC are not fully codegenned in `Database`; use default client for RPC. */
const supabaseAny = supabase as SupabaseClient;
import assessmentData from "@/data/fit2lift_uptime_assessment_v2.json";
import { flattenSectionQuestions, type AssessmentSection } from "@/lib/capacityAssessment";
import { painEraFor } from "@/lib/stiffSoreScoring";
import { buildSleepAggregate, type SleepAggregate } from "@/lib/stiffSoreAggregate";

const sections = assessmentData.sections as AssessmentSection[];

export type AssessmentSessionRow = {
  id: string;
  org_id: string;
  employee_db_id: string | null;
  /** Null for anonymous (lead_magnet) sessions, which carry no identifying data. */
  respondent_first_name: string | null;
  respondent_last_name: string | null;
  respondent_email: string | null;
  respondent_height_cm: number | null;
  respondent_weight_kg: number | null;
  respondent_dob: string | null;
  submitted_at: string;
  score_overall: number | null;
  score_neck: number | null;
  score_headache: number | null;
  score_mid_back: number | null;
  score_low_back: number | null;
  score_upper_extremity: number | null;
  score_lower_extremity: number | null;
  risk_band: string | null;
  risk_band_label: string | null;
  risk_band_hex: string | null;
  pain_score_neck: number | null;
  pain_score_headache: number | null;
  pain_score_mid_back: number | null;
  pain_score_low_back: number | null;
  pain_score_upper_ext: number | null;
  pain_score_lower_ext: number | null;
  /**
   * Which instrument produced this row. Load-bearing for BLOCKER-B on #67:
   * `pain_score_*` holds "average pain over the past week" for 2.0.0 rows and
   * "worst in the past 7 days" for 2.1.0. Optional because migrations are
   * applied by hand; absent reads as the legacy era via `painEraFor`.
   */
  assessment_version?: string | null;
  commitment_one_thing: string | null;
  /**
   * v2.1 (#71). "What have you stopped doing because of how your body feels?"
   * Individual-level free text. Feeds an anonymised quote bank (#72) — never
   * render it beside a location or job category, and never below the cohort
   * floor: either can identify a person in a small crew.
   */
  commitment_cost?: string | null;
  /** v2.1 (#71). Up to three region keys, in the respondent's own priority order. */
  priority_ranking?: string[] | null;
  /**
   * v2.2 Sleep Disturbance, 0-25 across five items. Runs the OPPOSITE way to
   * capacity: higher means more disturbed. Never averaged into score_overall.
   * Null for any session taken before v2.2.0, which is NOT the same as 0.
   */
  sleep_raw_score?: number | null;
  sleep_band?: "sleeping_well" | "minor" | "disrupted" | "badly" | "severe" | null;
  /** Reported nodding off, or nearly, during the working day. Count-only in aggregate. */
  sleep_safety_flag?: boolean | null;
  joint_selections: Record<string, unknown> | null;
  /** Snapshot of the org location selected on intake; used for MAPS filters. */
  location_snapshot_id: string | null;
  /** Snapshot of the org's job category selected on intake; used for MAPS filters. */
  job_category_snapshot_id: string | null;
  /**
   * Which data set this row belongs to; anonymous marketing rows are `lead_magnet`.
   * Optional because migrations are applied by hand: between deploying this code and
   * applying 07252026-01, `select("*")` will not return the column. Treat absent as
   * `client_full` (the column's DB default).
   */
  session_source?: "client_full" | "client_capacity_only" | "lead_magnet";
  /**
   * Null/absent counts toward reported aggregates; flagged rows are retained but
   * excluded, so reports keep an honest denominator. See 07252026-01.
   */
  quality_flag?: "all_zero" | "straight_line" | null;
  /** F2L-29: when the respondent affirmed voluntary participation (UTC ISO). */
  consent_captured_at?: string | null;
  /** Stable identifier for the consent text shown (e.g. assessment-consent-v1). */
  consent_version?: string | null;
  /** True when the respondent affirmed participation is voluntary. */
  consent_voluntary?: boolean | null;
};

/**
 * Capacity / pain questionnaire complete (MAPS "P").
 * Required for everyone under the voluntary-full-MAPS model (F2L-8).
 */
export function assessmentSessionHasCapacityComplete(
  s: AssessmentSessionRow | null | undefined,
): boolean {
  if (!s) return false;
  return s.score_overall != null && s.risk_band != null;
}

/**
 * @deprecated Prefer {@link assessmentSessionHasCapacityComplete}.
 * Historical name conflated questionnaire-done with full MAPS (posture+ROM+movement).
 */
export function assessmentSessionHasCompletedMaps(
  s: AssessmentSessionRow | null | undefined,
): boolean {
  return assessmentSessionHasCapacityComplete(s);
}

/**
 * v2.1 Stiff and Sore signals, one row per region per session (#71).
 *
 * Individual-level data with the same sensitivity as `AssessmentResponseRow`.
 * RLS gives an employer no read path to it at all; there is deliberately no
 * orgadmin or manager policy on the table. Employers see aggregates only.
 *
 * `stiff_sore_worst` duplicates `assessment_sessions.pain_score_*`, but unlike
 * that column it is unambiguous: it always means worst-in-7-days, never an
 * average. Prefer it for new work and use `painEraFor` when reading the legacy
 * column.
 */
export type AssessmentRegionSignalRow = {
  id: string;
  session_id: string;
  org_id: string;
  region_key: string;
  stiff_sore_worst: number | null;
  stiff_sore_days_7: number | null;
  /** Multi-select since v2.2; empty when skipped or screened out. */
  descriptors:
    | Array<"stiff" | "achy" | "sharp" | "burning" | "numb_tingling" | "weak">
    | null;
  trajectory: "better" | "same" | "worse" | "unsure" | null;
  /** True when the region screener was answered "no" and eleven zeros were auto-recorded. */
  screened_out: boolean;
  /** Derived server-side from the state matrix; never accepted from the client. */
  region_state: string | null;
  created_at: string;
};

export type AssessmentResponseRow = {
  id: string;
  session_id: string;
  org_id: string;
  question_id: string;
  section_id: string;
  region_key: string;
  raw_score: number;
  scale_max: number;
  item_type: string | null;
};

/** Question text + per-section sort order (PRD §8 — JSON + synthetic *-PAIN before Q8). */
const ASSESSMENT_Q_META = (() => {
  const textById = new Map<string, string>();
  const orderInSection = new Map<string, Map<string, number>>();
  for (const sec of sections) {
    const flat = flattenSectionQuestions(sec);
    const ids = flat.map((q) => q.id);
    const q8i = ids.findIndex((id) => id.endsWith("-Q8"));
    const first = sec.questions[0]?.id ?? "N-01";
    const prefix = first.split("-")[0] ?? "N";
    const painId = `${prefix}-PAIN`;
    const ordered = q8i >= 0 ? [...ids.slice(0, q8i), painId, ...ids.slice(q8i)] : [...ids, painId];
    const m = new Map<string, number>();
    ordered.forEach((id, i) => m.set(id, i));
    orderInSection.set(sec.section_id, m);
    for (const q of flat) {
      textById.set(q.id, q.text);
    }
    // Deliberately era-neutral. This map is keyed by question id alone and has
    // no access to the session's version, and the same id holds an AVERAGE for
    // v2.0.0 rows and a WORST for v2.1 rows. Naming either one here would
    // mislabel half the data. Use getAssessmentQuestionTextForEra where the
    // session is in hand.
    textById.set(
      painId,
      `0–10 ${sec.region.toLowerCase()} ache, stiffness or pain (0 = none, 10 = worst imaginable)`
    );
  }
  return { textById, orderInSection };
})();

export function getAssessmentQuestionText(questionId: string): string {
  return ASSESSMENT_Q_META.textById.get(questionId) ?? questionId;
}

/**
 * Question text that says which measurement the stored value actually is.
 *
 * Only the 0-10 item differs between eras; every other item is unchanged, so
 * this falls through to the shared text for them.
 */
export function getAssessmentQuestionTextForEra(
  questionId: string,
  assessmentVersion: string | null | undefined
): string {
  const base = getAssessmentQuestionText(questionId);
  if (!questionId.includes("-PAIN")) return base;
  return painEraFor(assessmentVersion) === "worst_v2_1"
    ? `${base} — worst over the past 7 days`
    : `${base} — average over the past week (v2.0.0 measure)`;
}

export function sortAssessmentResponses(rows: AssessmentResponseRow[]): AssessmentResponseRow[] {
  const { orderInSection } = ASSESSMENT_Q_META;
  return [...rows].sort((a, b) => {
    if (a.section_id !== b.section_id) return a.section_id.localeCompare(b.section_id);
    const ma = orderInSection.get(a.section_id)?.get(a.question_id) ?? 999;
    const mb = orderInSection.get(b.section_id)?.get(b.question_id) ?? 999;
    if (ma !== mb) return ma - mb;
    return a.question_id.localeCompare(b.question_id);
  });
}

/** Case-insensitive exact match for PostgREST `ilike`. */
function escapeILikeExact(s: string): string {
  return s.replace(/\\/g, "\\\\").replace(/%/g, "\\%").replace(/_/g, "\\_");
}

/** Sessions for one respondent email in an org (PRD §8.2 ED-002). */
export async function fetchAssessmentSessionsForRespondent(
  orgId: string,
  email: string
): Promise<AssessmentSessionRow[]> {
  // Employers never see individual sessions. RLS enforces this regardless; the
  // short-circuit just avoids a round trip that would return nothing.
  // CALLERS: an empty array here means "not visible to you", not "none exist".
  // Do not render it as a zero — use fetchOrgAssessmentAggregateFiltered.
  if (await currentUserIsOrgAdmin()) return [];
  const trimmed = email.trim();
  if (!trimmed) return [];
  const { data, error } = await supabase
    .from("assessment_sessions")
    .select("*")
    .eq("org_id", orgId)
    .ilike("respondent_email", escapeILikeExact(trimmed))
    .order("submitted_at", { ascending: false });
  if (error) throw error;
  return (data ?? []) as AssessmentSessionRow[];
}

/** Dedupe session rows by id — prefers newest submitted_at (merged org + respondent queries). */
export function dedupeAssessmentSessionsPreferNewest(rows: AssessmentSessionRow[]): AssessmentSessionRow[] {
  const map = new Map<string, AssessmentSessionRow>();
  for (const r of rows) {
    const prev = map.get(r.id);
    const t = new Date(r.submitted_at).getTime();
    const pt = prev ? new Date(prev.submitted_at).getTime() : -Infinity;
    if (!prev || t >= pt) map.set(r.id, r);
  }
  return [...map.values()].sort((a, b) => new Date(b.submitted_at).getTime() - new Date(a.submitted_at).getTime());
}

/**
 * Parallel org-wide + respondent-email reads, merged for roster linkage UX (employee mobile/desktop parity).
 */
export async function fetchAssessmentSessionsMergedForRespondentAndOrg(
  orgId: string,
  respondentEmail: string | null | undefined
): Promise<AssessmentSessionRow[]> {
  const trimmed = respondentEmail?.trim();
  const orgPromise = fetchAssessmentSessionsForOrg(orgId);
  const respondentPromise = trimmed
    ? fetchAssessmentSessionsForRespondent(orgId, trimmed).catch(() => [] as AssessmentSessionRow[])
    : Promise.resolve([] as AssessmentSessionRow[]);
  const [orgRows, respondentRows] = await Promise.all([orgPromise, respondentPromise]);
  return dedupeAssessmentSessionsPreferNewest([...respondentRows, ...orgRows]);
}

export async function fetchAssessmentSessionsForOrg(orgId: string): Promise<AssessmentSessionRow[]> {
  if (import.meta.env.DEV) {
    console.log("[Fit2Lift API] Query org filter: assessment_sessions", { orgId });
  }
  const { rows, truncated } = await fetchAllPagesBounded<AssessmentSessionRow>(async (from, to) => {
    const { data, error } = await supabase
      .from("assessment_sessions")
      .select("*")
      .eq("org_id", orgId)
      .order("submitted_at", { ascending: false })
      .range(from, to);
    if (error) throw error;
    return (data ?? []) as AssessmentSessionRow[];
  });
  if (truncated) {
    throw new Error(
      "Assessment session list exceeds the maximum loadable size for this organization.",
    );
  }
  return rows;
}

export async function fetchAssessmentResponsesForSession(sessionId: string): Promise<AssessmentResponseRow[]> {
  // Employers never see individual sessions. RLS enforces this regardless; the
  // short-circuit just avoids a round trip that would return nothing.
  // CALLERS: an empty array here means "not visible to you", not "none exist".
  // Do not render it as a zero — use fetchOrgAssessmentAggregateFiltered.
  if (await currentUserIsOrgAdmin()) return [];
  const { data, error } = await supabase
    .from("assessment_responses")
    .select("*")
    .eq("session_id", sessionId);
  if (error) throw error;
  return sortAssessmentResponses((data ?? []) as AssessmentResponseRow[]);
}

/** PRD §9 — single row from org_assessment_aggregate_filtered (or view-shaped). */
export type OrgAssessmentAggregateRow = {
  session_count: number;
  avg_overall: number | null;
  avg_neck: number | null;
  avg_headache: number | null;
  avg_mid_back: number | null;
  avg_low_back: number | null;
  avg_upper_ext: number | null;
  avg_lower_ext: number | null;
  avg_pain_neck: number | null;
  avg_pain_headache: number | null;
  avg_pain_mid_back: number | null;
  avg_pain_low_back: number | null;
  avg_pain_upper_ext: number | null;
  avg_pain_lower_ext: number | null;
  /**
   * v2.1 Stiff and Sore Scores (worst in 7 days), v2.1.0 rows ONLY.
   *
   * Deliberately separate from `avg_pain_*`, which now covers v2.0.0 rows only.
   * The two measure different things and were never comparable; before #72 they
   * were averaged into one number and shown to employers unlabelled.
   */
  avg_stiff_sore_neck: number | null;
  avg_stiff_sore_headache: number | null;
  avg_stiff_sore_mid_back: number | null;
  avg_stiff_sore_low_back: number | null;
  avg_stiff_sore_upper_ext: number | null;
  avg_stiff_sore_lower_ext: number | null;
  /** How many sessions each era contributed, so a UI can label a mixed cohort. */
  stiff_sore_session_count: number;
  legacy_pain_session_count: number;
  /**
   * v2.2 Sleep Disturbance. Carried as its own object rather than flattened,
   * because it is a separate module with its own scale and its own direction
   * (higher = worse) and must not be mistaken for another capacity figure.
   * Null when this aggregate came from the RPC, which does not return it yet.
   */
  sleep: SleepAggregate | null;
  /** null when suppressed below the small-cohort floor — see toRiskCount. */
  count_full: number | null;
  /**
   * v2.1 (#69). 85-91%: capacity slipping but not yet Reduced. Under v2.0.0
   * these respondents were counted in `count_full`, so a band distribution
   * spanning both eras will show an apparent drop in Full capacity that is a
   * boundary change, not a change in the workforce. Rendering this bucket is #72.
   */
  count_watch: number | null;
  count_reduced: number | null;
  count_at_risk: number | null;
  count_critical: number | null;
  first_assessment: string | null;
  latest_assessment: string | null;
};

function toFiniteNumber(v: unknown): number | null {
  if (v == null) return null;
  if (typeof v === "number" && Number.isFinite(v)) return v;
  if (typeof v === "string" && v.trim() !== "") {
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

function toSessionCount(v: unknown): number {
  if (typeof v === "bigint") return Number(v);
  if (typeof v === "number" && Number.isFinite(v)) return Math.max(0, Math.trunc(v));
  if (typeof v === "string" && v.trim() !== "") {
    const n = Math.trunc(Number(v));
    return Number.isFinite(n) && n >= 0 ? n : 0;
  }
  return 0;
}

/**
 * Risk-band counts are nullable on purpose, unlike session_count.
 *
 * org_assessment_aggregate_filtered withholds every figure except the
 * respondent count when a cohort is below the 10-person floor (see migration
 * 20260728000003), so null means "suppressed to protect a small group", not
 * zero. Coercing it to 0 produced "Roughly 0% flagged at-risk or critical" for a
 * cohort that may be entirely critical — a wrong number presented as a real one.
 */
function toRiskCount(v: unknown): number | null {
  if (v == null) return null;
  return toSessionCount(v);
}

function parseAggregateRpcRow(row: Record<string, unknown>): OrgAssessmentAggregateRow {
  return {
    session_count: toSessionCount(row.session_count),
    avg_overall: toFiniteNumber(row.avg_overall),
    avg_neck: toFiniteNumber(row.avg_neck),
    avg_headache: toFiniteNumber(row.avg_headache),
    avg_mid_back: toFiniteNumber(row.avg_mid_back),
    avg_low_back: toFiniteNumber(row.avg_low_back),
    avg_upper_ext: toFiniteNumber(row.avg_upper_ext),
    avg_lower_ext: toFiniteNumber(row.avg_lower_ext),
    avg_pain_neck: toFiniteNumber(row.avg_pain_neck),
    avg_pain_headache: toFiniteNumber(row.avg_pain_headache),
    avg_pain_mid_back: toFiniteNumber(row.avg_pain_mid_back),
    avg_pain_low_back: toFiniteNumber(row.avg_pain_low_back),
    avg_pain_upper_ext: toFiniteNumber(row.avg_pain_upper_ext),
    avg_pain_lower_ext: toFiniteNumber(row.avg_pain_lower_ext),
    avg_stiff_sore_neck: toFiniteNumber(row.avg_stiff_sore_neck),
    avg_stiff_sore_headache: toFiniteNumber(row.avg_stiff_sore_headache),
    avg_stiff_sore_mid_back: toFiniteNumber(row.avg_stiff_sore_mid_back),
    avg_stiff_sore_low_back: toFiniteNumber(row.avg_stiff_sore_low_back),
    avg_stiff_sore_upper_ext: toFiniteNumber(row.avg_stiff_sore_upper_ext),
    avg_stiff_sore_lower_ext: toFiniteNumber(row.avg_stiff_sore_lower_ext),
    stiff_sore_session_count: toSessionCount(row.stiff_sore_session_count),
    legacy_pain_session_count: toSessionCount(row.legacy_pain_session_count),
    // The RPC does not aggregate sleep yet; the panel renders "not yet
    // measured" for null rather than inventing a zero.
    sleep: null,
    count_full: toRiskCount(row.count_full),
    count_watch: toRiskCount(row.count_watch),
    count_reduced: toRiskCount(row.count_reduced),
    count_at_risk: toRiskCount(row.count_at_risk),
    count_critical: toRiskCount(row.count_critical),
    first_assessment: row.first_assessment != null ? String(row.first_assessment) : null,
    latest_assessment: row.latest_assessment != null ? String(row.latest_assessment) : null,
  };
}

const EMPTY_AGGREGATE: OrgAssessmentAggregateRow = {
  session_count: 0,
  avg_overall: null,
  avg_neck: null,
  avg_headache: null,
  avg_mid_back: null,
  avg_low_back: null,
  avg_upper_ext: null,
  avg_lower_ext: null,
  avg_pain_neck: null,
  avg_pain_headache: null,
  avg_pain_mid_back: null,
  avg_pain_low_back: null,
  avg_pain_upper_ext: null,
  avg_pain_lower_ext: null,
  avg_stiff_sore_neck: null,
  avg_stiff_sore_headache: null,
  avg_stiff_sore_mid_back: null,
  avg_stiff_sore_low_back: null,
  avg_stiff_sore_upper_ext: null,
  avg_stiff_sore_lower_ext: null,
  stiff_sore_session_count: 0,
  legacy_pain_session_count: 0,
  sleep: null,
  count_full: 0,
  count_watch: 0,
  count_reduced: 0,
  count_at_risk: 0,
  count_critical: 0,
  first_assessment: null,
  latest_assessment: null,
};

/**
 * Client-side rollup (same shape as `org_assessment_aggregate_filtered` RPC) — used when cohort
 * sessions are merged in the client (e.g. coherent demo) so MAPS and results stay aligned.
 */
export function orgAssessmentAggregateFromSessions(
  sessions: AssessmentSessionRow[]
): OrgAssessmentAggregateRow {
  if (sessions.length === 0) return { ...EMPTY_AGGREGATE };
  const n = sessions.length;
  const mean = (get: (s: AssessmentSessionRow) => number | null | undefined) => {
    const vals = sessions
      .map((s) => get(s))
      .filter((v): v is number => v != null && typeof v === "number" && Number.isFinite(v));
    if (vals.length === 0) return null;
    const s = vals.reduce((a, b) => a + b, 0);
    return Math.round((s / vals.length) * 10) / 10;
  };
  // BLOCKER-B on #67. pain_score_* holds two different measurements separated
  // only by the version stamp. Averaging them together produces a number that
  // means nothing, so each era is summarised on its own.
  const legacySessions = sessions.filter(
    (s) => painEraFor(s.assessment_version) === "average_v2_0"
  );
  const worstSessions = sessions.filter(
    (s) => painEraFor(s.assessment_version) === "worst_v2_1"
  );
  const meanOf = (
    rows: AssessmentSessionRow[],
    get: (s: AssessmentSessionRow) => number | null | undefined
  ) => {
    const vals = rows
      .map((s) => get(s))
      .filter((v): v is number => v != null && typeof v === "number" && Number.isFinite(v));
    if (vals.length === 0) return null;
    return Math.round((vals.reduce((a, b) => a + b, 0) / vals.length) * 10) / 10;
  };

  const countFull = sessions.filter((s) => s.risk_band === "full_capacity").length;
  const countWatch = sessions.filter((s) => s.risk_band === "watch").length;
  const countReduced = sessions.filter((s) => s.risk_band === "reduced").length;
  const countAtRisk = sessions.filter((s) => s.risk_band === "at_risk").length;
  const countCritical = sessions.filter((s) => s.risk_band === "critical").length;

  const times = sessions
    .map((s) => new Date(s.submitted_at).getTime())
    .filter((t) => Number.isFinite(t));
  const first = times.length ? new Date(Math.min(...times)).toISOString() : null;
  const latest = times.length ? new Date(Math.max(...times)).toISOString() : null;

  return {
    session_count: n,
    avg_overall: mean((s) => s.score_overall),
    avg_neck: mean((s) => s.score_neck),
    avg_headache: mean((s) => s.score_headache),
    avg_mid_back: mean((s) => s.score_mid_back),
    avg_low_back: mean((s) => s.score_low_back),
    avg_upper_ext: mean((s) => s.score_upper_extremity),
    avg_lower_ext: mean((s) => s.score_lower_extremity),
    avg_pain_neck: meanOf(legacySessions, (s) => s.pain_score_neck),
    avg_pain_headache: meanOf(legacySessions, (s) => s.pain_score_headache),
    avg_pain_mid_back: meanOf(legacySessions, (s) => s.pain_score_mid_back),
    avg_pain_low_back: meanOf(legacySessions, (s) => s.pain_score_low_back),
    avg_pain_upper_ext: meanOf(legacySessions, (s) => s.pain_score_upper_ext),
    avg_pain_lower_ext: meanOf(legacySessions, (s) => s.pain_score_lower_ext),
    avg_stiff_sore_neck: meanOf(worstSessions, (s) => s.pain_score_neck),
    avg_stiff_sore_headache: meanOf(worstSessions, (s) => s.pain_score_headache),
    avg_stiff_sore_mid_back: meanOf(worstSessions, (s) => s.pain_score_mid_back),
    avg_stiff_sore_low_back: meanOf(worstSessions, (s) => s.pain_score_low_back),
    avg_stiff_sore_upper_ext: meanOf(worstSessions, (s) => s.pain_score_upper_ext),
    avg_stiff_sore_lower_ext: meanOf(worstSessions, (s) => s.pain_score_lower_ext),
    stiff_sore_session_count: worstSessions.length,
    legacy_pain_session_count: legacySessions.length,
    sleep: buildSleepAggregate(
      sessions.map((s) => ({
        sleepRawScore: s.sleep_raw_score,
        sleepBand: s.sleep_band,
        sleepSafetyFlag: s.sleep_safety_flag,
      }))
    ),
    count_full: countFull,
    count_watch: countWatch,
    count_reduced: countReduced,
    count_at_risk: countAtRisk,
    count_critical: countCritical,
    first_assessment: first,
    latest_assessment: latest,
  };
}

/**
 * Thrown when the database refuses the aggregate for who you are — an
 * unassigned manager, a manager asking outside their scope, an employee.
 *
 * Distinct from a genuine failure on purpose. Callers should render "not
 * available to you" for this and an error state for anything else. What they
 * must NOT do is turn it into an empty result: a cohort of zero and a cohort you
 * are not allowed to see look identical to a user, and this codebase has already
 * shipped that mistake twice.
 */
export class AggregateNotPermittedError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "AggregateNotPermittedError";
  }
}

/** Postgres insufficient_privilege, raised by the RPC's own scope check. */
const PG_INSUFFICIENT_PRIVILEGE = "42501";

/**
 * PRD §9.2 — filtered org rollups (date range, plus optional location and job
 * category).
 *
 * This RPC is the ONLY sanctioned way for an org-side user to get assessment
 * figures. Do not query assessment_sessions directly for aggregates, and do not
 * fall back to the org_assessment_aggregates view — since 20260728000001 that
 * view obeys the caller's RLS and returns nothing to org-side users, and it has
 * no scope check or small-cohort floor of its own.
 *
 * Who the database allows (20260728000003 / 20260728000010): Fit2Lift staff
 * unrestricted; orgadmin and org_contact across their own organisation; a
 * manager only for a location or job category they are assigned to. Below 10
 * respondents every figure except session_count comes back null.
 */
export async function fetchOrgAssessmentAggregateFiltered(
  orgId: string,
  options: {
    locationId?: string | null;
    jobCategoryId?: string | null;
    since?: string | null;
  }
): Promise<OrgAssessmentAggregateRow> {
  const { data, error } = await supabaseAny.rpc("org_assessment_aggregate_filtered", {
    p_org_id: orgId,
    p_since: options.since ?? null,
    p_location_id: options.locationId?.trim() || null,
    p_job_category_id: options.jobCategoryId?.trim() || null,
  });
  if (error) {
    if ((error as { code?: string }).code === PG_INSUFFICIENT_PRIVILEGE) {
      throw new AggregateNotPermittedError(error.message);
    }
    throw error;
  }
  const raw = Array.isArray(data) ? data[0] : data;
  if (!raw || typeof raw !== "object") return { ...EMPTY_AGGREGATE };
  return parseAggregateRpcRow(raw as Record<string, unknown>);
}

export const assessmentSectionsForMaps = sections;
